import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {UrlService} from '@utils/services/url/url.service';
import {Clipboard} from '@angular/cdk/clipboard';
import {MatSnackBar} from '@angular/material/snack-bar';
import {isPlatformBrowser} from '@angular/common';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {
  MatchPollingService,
  MatchPollUpdateType,
} from '@lib-modules/match/services/match-polling/match-polling.service';

import {EntityType} from '@lib/models/entity/entity-type';
import {MatchRoutePaths} from '@lib-modules/match/match-route-paths';
import {Subscription} from 'rxjs';
import {MatchTickerService} from '@lib-modules/match/services/match-ticker/match-ticker.service';
import {MatchService} from '@lib-modules/match/services/match/match.service';
import { USER_CONTEXT, UserContext } from '../../../../../../utils/src/lib/context/user-context';

@Component({
  template: '',
  styleUrls: []
})
export abstract class MatchBaseComponent implements OnInit, OnDestroy {
  @Input() eventId: string;
  @Input() teamId: string;

  // Shared properties
  public loading = false;
  public menuOpen = false;
  public homeTeamLink: string;
  public awayTeamLink: string;
  public matchDate: Date;
  public entityType = EntityType.Team;
  private poll$: Subscription;
  protected hasInputParams = false;
  private user$: Subscription;

  @ViewChild('navElement', {read: ElementRef}) private navRef: ElementRef;

  @ViewChild('navElement', {read: ElementRef}) private set setNavRef(
    ref: ElementRef
  ) {
    if (ref && isPlatformBrowser(this.platformId)) {
      const parts = this.router.url.split('/');
      this.onNavClicked(parts[parts.length - 1], true);
    }
  }

  constructor(
    public matchService: MatchService,
    public adCampaignService: AdCampaignService,
    private matchPollingService: MatchPollingService,
    public matchTickerService: MatchTickerService,
    protected route: ActivatedRoute,
    protected router: Router,
    private clipboard: Clipboard,
    private snackbar: MatSnackBar,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(USER_CONTEXT) private userContext: UserContext,
  ) {
  }

  // Abstract methods that child components must implement
  protected abstract subscribeToRouteParams(): void;

  protected abstract onNavigateToDetail(): void;

  public async ngOnInit(): Promise<void> {
    await this.init();
    this.matchService.ready.emit();
    this.subscribeToRouteParams();
  }

  public ngOnDestroy(): void {
    this.stopPolling();
    this.user$?.unsubscribe();
  }

  // Shared methods
  public toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  public onNavClicked(path: string, noDelay = false): void {
    const element = this.navRef.nativeElement;
    if (
      path.includes(MatchRoutePaths.Infos) ||
      path.includes(MatchRoutePaths.Ticker) ||
      path.includes(MatchRoutePaths.Overview)
    ) {
      noDelay ? this.scrollLeft(element, -100) : this.scroll(element, -100);
    } else {
      noDelay ? this.scrollLeft(element, 100) : this.scroll(element, 100);
    }
  }

  public onCopyTickerLink(): void {
    this.clipboard.copy(this.matchService.match.short_url);
    this.snackbar.open('Link kopiert', null, {duration: 2500});
  }

  protected async init(): Promise<void> {
    this.setupUserObservable();
    if (this.eventId && this.teamId) {
      this.hasInputParams = true;
    }
    if (!this.eventId) {
      this.eventId = this.route.snapshot.paramMap.get('eventId');
    }
    if (!this.teamId) {
      this.teamId = this.route.snapshot.paramMap.get('teamId');
    }

    await this.matchService.init(this.eventId, this.teamId);
    this.eventId = this.matchService.match._id;
    await this.matchTickerService.init();

    const currentPath = this.route.snapshot.queryParamMap.get('tab');
    this.handleInitialNavigation(currentPath);
    const teams = this.matchService.teams;
    this.homeTeamLink = UrlService.getSubdomainUrl(teams.home.readable_id);
    this.awayTeamLink = UrlService.getSubdomainUrl(teams.away.readable_id);
    this.matchDate = new Date(this.matchService.match.timestamps.start);
    this.startPolling();
  }

  private setupUserObservable() {
    const subject = this.userContext?.userSubject;
    if (!subject) {
      this.matchService.user = null;
      return;
    }
    this.user$ = subject.subscribe(user => {
      this.matchService.user = user;
    });
  }

  private async handleInitialNavigation(currentPath: string) {
    if (this.matchService.isTabWithPathActive(currentPath)) {
      return;
    }
    if (this.matchService.isTabWithPathActive(MatchRoutePaths.Ticker)) {
      await this.router.navigate([MatchRoutePaths.Ticker], {relativeTo: this.route, replaceUrl: true});
    } else {
      await this.router.navigate([MatchRoutePaths.Infos], {relativeTo: this.route, replaceUrl: true});
    }
  }


  private startPolling(): void {
    const poll = this.matchPollingService.add(
      this.matchService.match,
      this.matchService.selectedTeamId,
      [
        MatchPollUpdateType.Status,
        MatchPollUpdateType.Tabs,
        MatchPollUpdateType.Timeline,
      ]
    );
    this.poll$ = poll?.updates.subscribe((update) => {
      if (!update) {
        return;
      }
      if (update.type === MatchPollUpdateType.Status) {
        this.matchService.updateStatus(update.data);
      }
      if (update.type === MatchPollUpdateType.Tabs) {
        this.matchService.updateTabs(update.data);
      }
      if (update.type === MatchPollUpdateType.Timeline) {
        this.matchTickerService.updateItems(update.data);
      }
    });
  }

  private stopPolling(): void {
    this.matchPollingService.remove(this.matchService.match);
    this.poll$?.unsubscribe();
  }

  protected async reload(eventId: string, teamId: string): Promise<void> {
    this.loading = true;
    this.stopPolling();
    this.eventId = eventId;
    this.teamId = teamId;
    await this.init();
    this.matchService.ready.emit();
    this.loading = false;
  }

  private scrollLeft(element, value): void {
    requestAnimationFrame(() => (element.scrollLeft = value));
  }

  private scroll(element, amount, time = 300): void {
    const eAmt = amount / 100;
    let currentTime = 0;
    let scrollCounter = 0;
    while (currentTime <= time) {
      setTimeout(
        this.scrollLeft,
        currentTime,
        element,
        eAmt * scrollCounter + element.scrollLeft
      );
      currentTime += time / 100;
      scrollCounter++;
    }
  }

  protected navigateBack() {
    this.location.back();
  }
}
